import { isDefined } from "@clipboard-health/util-ts";
import { type FeedItem } from "@knocklabs/client";
import { useKnockFeed, useNotificationStore } from "@knocklabs/react";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logError } from "@src/appV2/lib/analytics/log";
import {
  addAppResumeListener,
  type AppListenerHandle,
} from "@src/appV2/NativeAdapters/app.adapter";
import {
  FCF_DEEP_LINK_PARAM_KEYS,
  type FcfDeepLinkParams,
} from "@src/appV2/redesign/WorkerShifts/FcfReplacementShifts/types";
import { useEffect, useMemo, useState } from "react";

const FCF_WORKFLOW_KEY = "fcf-recommendation-workplace-cancelled-shift";
const FCF_EXPIRY_MS = 4 * 60 * 60 * 1000;

export interface FcfFeedItem {
  message: FeedItem;
  params: FcfDeepLinkParams;
}

export interface UseFcfKnockFeedMessagesResult {
  currentItem: FcfFeedItem | undefined;
  onDismiss: () => Promise<void>;
  onInteract: () => Promise<void>;
  onArchive: () => Promise<void>;
}

function parseFcfParams(link: string): FcfDeepLinkParams | undefined {
  try {
    const url = new URL(link);
    const qualification = url.searchParams.get(FCF_DEEP_LINK_PARAM_KEYS.qualification);
    const shiftStart = url.searchParams.get(FCF_DEEP_LINK_PARAM_KEYS.shiftStart);
    const cancelledWorkplaceId = url.searchParams.get(
      FCF_DEEP_LINK_PARAM_KEYS.cancelledWorkplaceId
    );

    if (!isDefined(qualification) || !isDefined(shiftStart) || !isDefined(cancelledWorkplaceId)) {
      return undefined;
    }

    return { qualification, shiftStart, cancelledWorkplaceId };
  } catch {
    return undefined;
  }
}

function buildQueue(items: FeedItem[], expiredIds: ReadonlySet<string>): FcfFeedItem[] {
  const now = Date.now();
  return items
    .filter((item) => {
      if (item.source.key !== FCF_WORKFLOW_KEY) {
        return false;
      }
      if (isDefined(item.interacted_at)) {
        return false;
      }
      if (expiredIds.has(item.id)) {
        return false;
      }
      if (now - new Date(item.inserted_at).getTime() >= FCF_EXPIRY_MS) {
        return false;
      }
      return true;
    })
    .flatMap((item) => {
      const link = (item.data as { link?: string } | null)?.link;
      if (!isDefined(link)) {
        return [];
      }
      const params = parseFcfParams(link);
      if (!isDefined(params)) {
        return [];
      }
      return [{ message: item, params }];
    });
}

export function useFcfKnockFeedMessages(): UseFcfKnockFeedMessagesResult {
  const { feedClient } = useKnockFeed();
  const items = useNotificationStore(feedClient, (state) => state.items);

  // Track IDs we've already handled client-side to avoid re-queuing them
  // while the Knock state propagates back.
  const [expiredIds, setExpiredIds] = useState<ReadonlySet<string>>(new Set());

  const queue = useMemo(() => buildQueue(items, expiredIds), [items, expiredIds]);

  useEffect(() => {
    let cancelled = false;
    let handle: AppListenerHandle | undefined;

    async function fetchFeed(): Promise<void> {
      try {
        await feedClient.fetch();
      } catch (error) {
        if (!cancelled) {
          logError(APP_V2_APP_EVENTS.KNOCK_FCF_FEED_FETCH_FAILED, { error });
        }
      }
    }

    async function registerResumeListener(): Promise<void> {
      try {
        handle = await addAppResumeListener(async () => {
          if (!cancelled) {
            await fetchFeed();
          }
        });
        // Cleanup may have run while we were awaiting — remove immediately if so.
        if (cancelled) {
          void handle.remove();
        }
      } catch (error) {
        if (!cancelled) {
          logError(APP_V2_APP_EVENTS.KNOCK_FCF_FEED_ATTACH_RESUME_LISTENER_FAILED, { error });
        }
      }
    }

    void fetchFeed();
    void registerResumeListener();

    return () => {
      cancelled = true;
      void handle?.remove();
    };
  }, [feedClient]);

  // For now we only show the first queued item. The queue structure is kept
  // intentionally so a follow-up can show them sequentially without refactoring.
  const [currentItem] = queue;

  async function onDismiss(): Promise<void> {
    if (!isDefined(currentItem)) {
      return;
    }
    setExpiredIds((previous) => new Set([...previous, currentItem.message.id]));
    try {
      await feedClient.markAsArchived(currentItem.message);
    } catch (error) {
      logError(APP_V2_APP_EVENTS.KNOCK_FCF_FEED_ARCHIVE_FAILED, { error });
    }
  }

  async function onInteract(): Promise<void> {
    if (!isDefined(currentItem)) {
      return;
    }
    setExpiredIds((previous) => new Set([...previous, currentItem.message.id]));
    try {
      await feedClient.markAsInteracted(currentItem.message);
    } catch (error) {
      logError(APP_V2_APP_EVENTS.KNOCK_FCF_FEED_INTERACT_FAILED, { error });
    }
  }

  async function onArchive(): Promise<void> {
    if (!isDefined(currentItem)) {
      return;
    }
    try {
      await feedClient.markAsArchived(currentItem.message);
    } catch (error) {
      logError(APP_V2_APP_EVENTS.KNOCK_FCF_FEED_ARCHIVE_FAILED, { error });
    }
  }

  return { currentItem, onDismiss, onInteract, onArchive };
}
